import { HDate, HebrewCalendar, Sedra, flags as F, parshiot } from '@hebcal/core';

// Hebrew month names
const MONTH_NAMES_HE = {
  1: 'ניסן', 2: 'אייר', 3: 'סיוון', 4: 'תמוז', 5: 'אב', 6: 'אלול',
  7: 'תשרי', 8: 'חשוון', 9: 'כסלו', 10: 'טבת', 11: 'שבט', 12: 'אדר',
  13: 'אדר א׳', 14: 'אדר ב׳',
};

// Adar in non-leap year is month 12; in leap year 13=Adar I, 14=Adar II
// HDate uses: 1-6 = Nisan..Elul, 7-12 = Tishrei..Adar, 13 = Adar I (leap), 14 = Adar II (leap)
// Actually HDate months: NISAN=1..ADAR_II=13 for leap, ADAR=12 for non-leap
// Let's use HDate's own getMonth()

const DAY_NAMES_HE = ['יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי', 'יום שישי', 'יום שבת'];

/**
 * Extract calendar data from HDate + HebrewCalendar events.
 *
 * @param {HDate} hdate - the halachic Hebrew date
 * @param {boolean} isIsrael
 * @returns {object} calendar data
 */
export function getCalendarData(hdate, isIsrael) {
  const year = hdate.getFullYear();
  const month = hdate.getMonth();
  const day = hdate.getDate();
  const dayOfWeek = hdate.getDay(); // 0=Sun, 6=Sat
  const isLeapYear = HDate.isLeapYear(year);

  // Get events for this date
  const events = HebrewCalendar.calendar({
    start: hdate,
    end: hdate,
    il: isIsrael,
    sedrot: true,
    omer: true,
    shabbatMevarchim: true,
    yomKippurKatan: false,
    molad: false,
  });

  // Hebrew date display
  const hebrewDate = {
    hebrew: hdate.renderGematriya(true),
    hebrewShort: hdate.renderGematriya(false),
    day,
    month,
    monthName: getMonthName(month, isLeapYear),
    year,
    dayOfWeek,
    dayOfWeekHebrew: DAY_NAMES_HE[dayOfWeek],
    isLeapYear,
  };

  // Parse events
  const isShabbat = dayOfWeek === 6;
  let isRoshChodesh = false;
  let isYomTov = false;
  let isCholHamoed = false;
  let isFastDay = false;
  let isChanukah = false;
  let specialShabbat = null;
  let shabbatMevarchim = false;
  let mevarchimMonth = null;
  let omerDay = null;
  let isIsruChag = false;

  const holidays = [];

  for (const ev of events) {
    const f = ev.getFlags();
    const desc = ev.desc;
    const heDesc = ev.renderBrief('he');

    // Rosh Chodesh
    if (f & F.ROSH_CHODESH) {
      isRoshChodesh = true;
      holidays.push({ name: heDesc, nameEn: desc, category: 'roshChodesh', isNidcheh: false });
    }

    // Chag (Yom Tov)
    if (f & F.CHAG) {
      isYomTov = true;
      holidays.push({ name: heDesc, nameEn: desc, category: 'major', isNidcheh: false });
    }

    // Chol HaMoed
    if (f & F.CHOL_HAMOED) {
      isCholHamoed = true;
      if (!(f & F.CHAG)) {
        holidays.push({ name: heDesc, nameEn: desc, category: 'cholHamoed', isNidcheh: false });
      }
    }

    // Fast days
    if (f & F.MINOR_FAST) {
      isFastDay = true;
      holidays.push({ name: heDesc, nameEn: desc, category: 'fast', isNidcheh: desc.includes('(observed)') });
    }
    if (f & F.MAJOR_FAST) {
      isFastDay = true;
      holidays.push({ name: heDesc, nameEn: desc, category: 'fast', isNidcheh: desc.includes('(observed)') });
    }

    // Chanukah
    if (f & F.CHANUKAH_CANDLES || desc.includes('Chanukah')) {
      isChanukah = true;
      if (!holidays.some(h => h.nameEn.includes('Chanukah') && h.category !== 'roshChodesh')) {
        holidays.push({ name: heDesc, nameEn: desc, category: 'minor', isNidcheh: false });
      }
    }

    // Special Shabbat
    if (f & F.SPECIAL_SHABBAT) {
      specialShabbat = heDesc;
    }

    // Shabbat Mevarchim
    if (f & F.SHABBAT_MEVARCHIM) {
      shabbatMevarchim = true;
      // Extract month name from desc: "Shabbat Mevarchim Chodesh X"
      const match = desc.match(/Chodesh (.+)/);
      mevarchimMonth = match ? heDesc.replace(/מְבָרְכִים חוֹדֶשׁ\s*/, '') : null;
    }

    // Omer
    if (f & F.OMER_COUNT) {
      const omerMatch = desc.match(/(\d+)/);
      if (omerMatch) omerDay = parseInt(omerMatch[1], 10);
    }

    // Minor holidays
    if (f & F.MINOR_HOLIDAY) {
      if (!holidays.some(h => h.nameEn === desc)) {
        holidays.push({ name: heDesc, nameEn: desc, category: 'minor', isNidcheh: false });
      }
    }

    // Modern holidays
    if (f & F.MODERN_HOLIDAY) {
      if (!holidays.some(h => h.nameEn === desc)) {
        holidays.push({ name: heDesc, nameEn: desc, category: 'modern', isNidcheh: false });
      }
    }
  }

  // Isru Chag detection: day after last day of YT
  isIsruChag = checkIsruChag(hdate, isIsrael);

  // Parsha (only on Shabbat)
  let parsha = null;
  if (isShabbat) {
    // Get Hebrew parsha name from PARSHA_HASHAVUA event
    const parshaEvent = events.find(e => e.getFlags() & F.PARSHA_HASHAVUA);
    if (parshaEvent) {
      const parshaHe = parshaEvent.renderBrief('he')
        .replace(/^פָּרָשַׁת\s+/, '')
        .replace(/^פרשת\s+/, '');
      const sedra = new Sedra(year, isIsrael);
      const lookup = sedra.lookup(hdate);
      const parshaNames = lookup.parsha || [];
      parsha = {
        name: parshaHe,
        nameEn: parshaNames.join('-'),
        isDouble: parshaNames.length > 1,
      };
    }
  }

  return {
    hebrewDate,
    parsha,
    holidays,
    isShabbat,
    isYomTov,
    isCholHamoed,
    isRoshChodesh,
    isFastDay,
    isChanukah,
    isIsruChag,
    specialShabbat,
    shabbatMevarchim,
    mevarchimMonth,
    omerDay,
    events, // pass raw events for downstream use
  };
}

/**
 * Check if the given date is Isru Chag.
 * Isru Chag = day after last day of Pesach/Shavuot/Sukkot.
 */
function checkIsruChag(hdate, isIsrael) {
  const yesterday = new HDate(hdate.abs() - 1);
  const yMonth = yesterday.getMonth();
  const yDay = yesterday.getDate();

  // After Pesach: 21 Nisan (IL) or 22 Nisan (diaspora)
  if (yMonth === 1 && yDay === (isIsrael ? 21 : 22)) return true;
  // After Shavuot: 6 Sivan (IL) or 7 Sivan (diaspora)
  if (yMonth === 3 && yDay === (isIsrael ? 6 : 7)) return true;
  // After Sukkot: 22 Tishrei (IL) or 23 Tishrei (diaspora)
  if (yMonth === 7 && yDay === (isIsrael ? 22 : 23)) return true;

  return false;
}

/**
 * Get Hebrew month name.
 */
function getMonthName(month, isLeapYear) {
  if (!isLeapYear && month === 12) return 'אדר';
  if (isLeapYear && month === 12) return 'אדר א׳';
  if (isLeapYear && month === 13) return 'אדר ב׳';
  return MONTH_NAMES_HE[month] || '';
}

/**
 * Get calendar events for a date range (used for motzaei Shabbat look-ahead).
 */
export function getEventsInRange(startHDate, endHDate, isIsrael) {
  return HebrewCalendar.calendar({
    start: startHDate,
    end: endHDate,
    il: isIsrael,
  });
}

/**
 * Compute shabbatTitle convenience field.
 */
export function computeShabbatTitle(calData) {
  if (!calData.isShabbat) return { shabbatTitle: null, shabbatTitleType: null };

  const { parsha, isCholHamoed, isYomTov, isRoshChodesh, isChanukah, specialShabbat, holidays } = calData;
  const base = parsha ? parsha.name : null;

  // Find the base holiday name for CHM / YT labels
  const getHolidayBaseName = () => {
    const chag = holidays.find(h => h.category === 'major' || h.category === 'cholHamoed');
    if (!chag) return '';
    // Extract base name: "פֶּסַח ב׳ (חוה״מ)" → "פסח"
    const name = chag.name;
    if (name.includes('פסח') || name.includes('פֶּסַח')) return 'פסח';
    if (name.includes('סוכות') || name.includes('סֻכּוֹת')) return 'סוכות';
    return name;
  };

  // Priority 1: No parsha — Chol HaMoed or Yom Tov replaces parsha
  if (!base && isCholHamoed) {
    return {
      shabbatTitle: 'שבת חול המועד ' + getHolidayBaseName(),
      shabbatTitleType: 'holiday',
    };
  }

  if (!base && isYomTov) {
    const ytHoliday = holidays.find(h => h.category === 'major');
    const ytName = ytHoliday ? ytHoliday.name : '';
    return {
      shabbatTitle: 'שבת ו' + ytName,
      shabbatTitleType: 'holiday',
    };
  }

  // Priority 2: Parsha + special event combo
  if (base && specialShabbat) {
    return {
      shabbatTitle: base,
      shabbatTitleType: 'combo',
      shabbatSubtitle: specialShabbat,
    };
  }

  if (base && isRoshChodesh) {
    return {
      shabbatTitle: base,
      shabbatTitleType: 'combo',
      shabbatSubtitle: 'שבת ראש חודש',
    };
  }

  if (base && isChanukah) {
    return {
      shabbatTitle: base,
      shabbatTitleType: 'combo',
      shabbatSubtitle: 'שבת חנוכה',
    };
  }

  // Priority 3: Plain parsha
  if (base) {
    return {
      shabbatTitle: base,
      shabbatTitleType: 'parsha',
    };
  }

  // Fallback — should not normally reach here
  return { shabbatTitle: 'שבת', shabbatTitleType: 'holiday' };
}
